import React__default from 'react';
import { useSheetFields, getTranslatedAndSortedMediaFields, dataViewElementsAuthorizedIconsConfiguration, dataViewElementsVisualAlertConfiguration, tagsConfigurationAtom, dataViewElementsThumbnailSizeConfiguration, fetcherModeAtom, FetcherMode, currentLocaleAtom, dataViewElementsLinksConfiguration, useBounceSearch, useFilterReset, Position, MediaField, DataViewKey, isMediaField, SpecificDataviewType, createFolderBusinessFilter } from '@keepeek/commons';
import { Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import ElementCardMedia from '../../../ElementCardMedia/index.js';
import { ElementMetaField } from '../../../ElementMetaField/index.js';
import { FormatTechnicalField } from '../../../FormatTechnicalFields/index.js';
import FormatTechnicalFolders, { DisplayMode } from '../../../FormatTechnicalFields/FormatTechnicalFolders.js';
import { isTouchDevice } from '../../../utils/touchDevice.js';
import ElementActions from '../../Elements/ElementActions/index.js';
import ElementDuration from '../../Elements/ElementDuration/index.js';
import ElementLinks from '../../Elements/ElementLinks/index.js';
import IconOverMedia from '../../Elements/IconOverMedia/index.js';
import Tags from '../../Tags/index.js';
import { getTagsDataByPosition } from '../../Tags/utils/getTagsDataByPosition.js';
import { getVisualAlertsDataByPosition } from '../../Tags/utils/getVisualAlertsDataByPosition.js';
import { CellType } from './ListDataViewRow/index.js';

var useColumnsResolver = function (_a) {
    var _b, _c, _d;
    var KIcon = _a.KIcon, shareTokenDownloadAuthorized = _a.shareTokenDownloadAuthorized, sentryCallback = _a.sentryCallback, listViewFields = _a.listViewFields, _e = _a.showLinks, showLinks = _e === void 0 ? true : _e, _f = _a.showTags, showTags = _f === void 0 ? true : _f, allowSelection = _a.allowSelection, allowNavigation = _a.allowNavigation, dataViewKey = _a.dataViewKey;
    var t = useTranslation("refront-components").t;
    var sheetFields = useSheetFields().sheetFields;
    var translatedMediaFields = getTranslatedAndSortedMediaFields();
    var columnHelper = createColumnHelper();
    var authorizedIcons = useRecoilValue(dataViewElementsAuthorizedIconsConfiguration);
    var visualAlertsConf = useRecoilValue(dataViewElementsVisualAlertConfiguration);
    var tagsConfiguration = useRecoilValue(tagsConfigurationAtom);
    var isTagEnabled = tagsConfiguration !== null &&
        (tagsConfiguration === null || tagsConfiguration === void 0 ? void 0 : tagsConfiguration.tagsToDisplayId) !== null &&
        ((_b = tagsConfiguration === null || tagsConfiguration === void 0 ? void 0 : tagsConfiguration.tagsToDisplayId) === null || _b === void 0 ? void 0 : _b.length) !== 0;
    var thumbnailSize = useRecoilValue(dataViewElementsThumbnailSizeConfiguration);
    var fetcherMode = useRecoilValue(fetcherModeAtom);
    var isShare = fetcherMode === FetcherMode.SHARE;
    var currentLocale = useRecoilValue(currentLocaleAtom);
    var _g = (_c = useRecoilValue(dataViewElementsLinksConfiguration)) !== null && _c !== void 0 ? _c : {
        showMediaLink: false,
        showAttachment: false
    }, showMediaLink = _g.showMediaLink, showAttachment = _g.showAttachment;
    var handleBounceClick = useBounceSearch(dataViewKey, sentryCallback);
    var resetFilters = useFilterReset(dataViewKey);
    var getAccessor = function (row, field) {
        var _a;
        return (_a = row.fields) === null || _a === void 0 ? void 0 : _a.find(function (f) { return f.id === field; });
    };
    function getHeader(field) {
        // This to allow override of all headers name
        var key = "components.dataview.list.header.".concat(field);
        var value = t(key);
        if (value !== key) {
            return value;
        }
        var mediaField = translatedMediaFields === null || translatedMediaFields === void 0 ? void 0 : translatedMediaFields.find(function (f) { return f.id === field; });
        if (mediaField) {
            if (mediaField.id === MediaField.WIDTH) {
                return t("mediafield_dimensions");
            }
            return mediaField.name;
        }
        var metaField = sheetFields === null || sheetFields === void 0 ? void 0 : sheetFields.find(function (f) { return f.id === field; });
        if (metaField) {
            return metaField.title;
        }
        return "";
    }
    function handleFolderClick(folder, e) {
        var _a;
        e.stopPropagation();
        var businessFilter = createFolderBusinessFilter(folder.id, (_a = folder === null || folder === void 0 ? void 0 : folder.title) !== null && _a !== void 0 ? _a : "", t("components.business-filter.advanced.folder.label"), folder.path);
        resetFilters({
            exceptFolderFilter: false,
            exceptBasketFilter: dataViewKey === DataViewKey.BASKET_DETAIL,
            filterToAddAfterReset: businessFilter
        });
    }
    var columns = [];
    columns.push(
    // Using CellType.MEDIA cause typescript issue with tanstack here, so to avoid having to use ts-ignore
    // we just use the string directly
    columnHelper.accessor("previewUrl", {
        cell: function (info) {
            var _a;
            var element = info.row.original;
            var visualAlertsDataByPosition = isTagEnabled
                ? undefined
                : getVisualAlertsDataByPosition(visualAlertsConf, element, currentLocale);
            var tagsDataByPosition = isTagEnabled
                ? getTagsDataByPosition(tagsConfiguration, element.labels, isNil(tagsConfiguration === null || tagsConfiguration === void 0 ? void 0 : tagsConfiguration.visibleTagsCount) ? 3 : tagsConfiguration === null || tagsConfiguration === void 0 ? void 0 : tagsConfiguration.visibleTagsCount)
                : undefined;
            // We want to minimize tags title only for the case of labels that come from the api (not for the visual alerts)
            var minimizeTagsTitles = tagsDataByPosition !== undefined;
            var tagsToDisplay = visualAlertsDataByPosition || tagsDataByPosition;
            var _b = info, hover = _b.hover, onSelection = _b.onSelection, isSelected = _b.isSelected, selectionMode = _b.selectionMode, isEntered = _b.isEntered;
            return (React__default.createElement(ElementCardMedia, { element: element !== null && element !== void 0 ? element : undefined, allowSelection: allowSelection && (!isShare || (isShare && shareTokenDownloadAuthorized)), hover: hover, onSelect: onSelection, isSelected: isSelected, selectionMode: selectionMode || isTouchDevice(), imageSize: thumbnailSize, isEntered: isEntered, iconOverMedia: React__default.createElement(IconOverMedia, { element: element, KIcon: KIcon, authorizedIcons: authorizedIcons }), topLeftElement: showTags &&
                    ((tagsToDisplay === null || tagsToDisplay === void 0 ? void 0 : tagsToDisplay.has(Position.TopLeft)) ? (React__default.createElement(Tags, { tagsPosition: Position.TopLeft, tagsData: tagsToDisplay.get(Position.TopLeft) || [], titleMinimized: minimizeTagsTitles })) : null), topRightElement: showTags &&
                    ((tagsToDisplay === null || tagsToDisplay === void 0 ? void 0 : tagsToDisplay.has(Position.TopRight)) ? (React__default.createElement(Tags, { tagsPosition: Position.TopRight, tagsData: tagsToDisplay.get(Position.TopRight) || [], titleMinimized: minimizeTagsTitles })) : null), bottomLeftElement: (_a = element === null || element === void 0 ? void 0 : element.fields) === null || _a === void 0 ? void 0 : _a.map(function (f) {
                    if (((element === null || element === void 0 ? void 0 : element.mediaType.startsWith("video/")) ||
                        (element === null || element === void 0 ? void 0 : element.mediaType.startsWith("audio/"))) &&
                        f.id === MediaField.DURATION_IN_SECONDS.toString()) {
                        return React__default.createElement(ElementDuration, { key: f.id, value: f.value });
                    }
                    else {
                        return null;
                    }
                }), allowNavigation: allowNavigation, KIcon: KIcon }));
        },
        header: function () {
            return getHeader("media");
        }
    }));
    (_d = listViewFields === null || listViewFields === void 0 ? void 0 : listViewFields["default"]) === null || _d === void 0 ? void 0 : _d.map(function (fieldName) {
        return columns.push(columnHelper.accessor(function (row) { return getAccessor(row, fieldName); }, {
            id: fieldName,
            cell: function (info) {
                var element = info.row.original;
                var field = info.getValue();
                if (fieldName === MediaField.FOLDER_ID) {
                    return (React__default.createElement(FormatTechnicalFolders, { folders: element.folders, displayMode: dataViewKey === DataViewKey.BASKET_DETAIL
                            ? DisplayMode.FIRST_FOLDER_WITHOUT_LINK
                            : DisplayMode.FIRST_FOLDER_WITH_LINK, onFolderClick: handleFolderClick, KIcon: KIcon }));
                }
                if (isMediaField(fieldName)) {
                    return React__default.createElement(FormatTechnicalField, { field: field, fields: element.fields });
                }
                if (field) {
                    return (React__default.createElement(ElementMetaField, { field: field, summaryDisplay: false, showTitle: false, disableClamping: true, onClick: !isShare
                            ? function (value) {
                                handleBounceClick(field, value);
                            }
                            : undefined, KIcon: KIcon }));
                }
                return null;
            },
            header: function () {
                return (React__default.createElement(Box, { title: getHeader(fieldName), sx: { overflow: "hidden", textOverflow: "ellipsis" } }, getHeader(fieldName)));
            }
        }));
    });
    if (showLinks && !isShare && (showMediaLink || showAttachment)) {
        columns.push(columnHelper.display({
            id: CellType.LINKS,
            cell: function (info) {
                var _a = info, showAttachmentLink = _a.showAttachmentLink, showMediaLinkLink = _a.showMediaLinkLink, associationClick = _a.associationClick, actionClick = _a.actionClick;
                return (React__default.createElement(ElementLinks, { actionClick: actionClick, associationClick: associationClick, displayAttachmentsButton: showAttachmentLink, displayMediaLinksButton: showMediaLinkLink, dataviewType: SpecificDataviewType.LIST }));
            },
            header: function () {
                return getHeader("links");
            }
        }));
    }
    columns.push(columnHelper.display({
        id: CellType.ACTIONS,
        cell: function (info) {
            var _a = info, showAddToBasketButton = _a.showAddToBasketButton, showRemoveFromBasketButton = _a.showRemoveFromBasketButton, showDownloadButton = _a.showDownloadButton, showShareButton = _a.showShareButton, showAttachmentButton = _a.showAttachmentButton, showInsertButton = _a.showInsertButton, actionClick = _a.actionClick, selectionMode = _a.selectionMode, showThumbnailGeneratorButton = _a.showThumbnailGeneratorButton, actionButtonsColor = _a.actionButtonsColor, disableElementActionsAnimation = _a.disableElementActionsAnimation;
            return (React__default.createElement(ElementActions, { displayAddToBasketAction: showAddToBasketButton, displayRemoveFromBasketAction: showRemoveFromBasketButton, displayDownloadAction: showDownloadButton, displayShareAction: showShareButton, displayShowAttachmentButton: showAttachmentButton, KIcon: KIcon, onActionClick: actionClick, displayInsertAction: showInsertButton, disabled: selectionMode, displayShowThumbnailGeneratorAction: showThumbnailGeneratorButton, buttonsColor: actionButtonsColor, disableElementActionsAnimation: disableElementActionsAnimation }));
        },
        header: function () {
            return getHeader("buttons");
        }
    }));
    return columns;
};
var useTitleFieldInColumnsResolver = function (_a) {
    var KIcon = _a.KIcon, sentryCallback = _a.sentryCallback, _b = _a.showLinks, showLinks = _b === void 0 ? true : _b, _c = _a.showTags, showTags = _c === void 0 ? true : _c, dataViewKey = _a.dataViewKey;
    var fields = {
        "default": ["title"]
    };
    return useColumnsResolver({
        KIcon: KIcon,
        shareTokenDownloadAuthorized: false,
        listViewFields: fields,
        sentryCallback: sentryCallback,
        showLinks: showLinks,
        showTags: showTags,
        dataViewKey: dataViewKey
    });
};

export { useColumnsResolver, useTitleFieldInColumnsResolver };
//# sourceMappingURL=utils.js.map
